/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   Short-term cancellation bizos
 */

import { Bizo, autoclass } from './base'
import type {
  CancellationPolicy as CancellationPolicyType,
  FlatCancellationPolicy,
  PercentageCancellationPolicy,
  RawCancellationPolicy,
  RawShortTermCancellationPolicies,
  ShortTermCancellationPoliciesError,
  ShortTermCancellationPoliciesForReal,
  ShortTermCancellationPolicies as ShortTermCancellationPoliciesType,
} from './shortTermCancellation.types'

export const CancellationPolicy = autoclass<CancellationPolicyType, RawCancellationPolicy>(
  'CancellationPolicy',
  class CancellationPolicy extends Bizo<CancellationPolicyType, RawCancellationPolicy> {},
  {
    absoluteAmount: 'absolute_amount',
    cancellationCostType: 'cancellation_cost_type',
    description: 'description',
    endDate: 'end_date',
    percentageAmount: 'percentage_amount',
    startDate: 'start_date',
  }
) as unknown as typeof FlatCancellationPolicy & typeof PercentageCancellationPolicy

export type CancellationPolicy = CancellationPolicyType

function isCancellationsArray(
  cancellations: RawShortTermCancellationPolicies['cancellations']
): cancellations is readonly RawCancellationPolicy[] {
  return !!cancellations[0] && typeof cancellations[0] === 'object'
}

export const ShortTermCancellationPolicies = autoclass<
  ShortTermCancellationPoliciesType,
  RawShortTermCancellationPolicies
>(
  'ShortTermCancellationPolicies',
  class ShortTermCancellationPolicies extends Bizo<
    ShortTermCancellationPoliciesType,
    RawShortTermCancellationPolicies
  > {},
  {
    cancellations: ({ _data: { cancellations } }: ShortTermCancellationPoliciesType) => {
      if (!Array.isArray(cancellations)) {
        return []
      }
      if (!isCancellationsArray(cancellations)) {
        return cancellations
      }
      return cancellations.map((cancellation) =>
        cancellation.cancellation_cost_type
          ? new CancellationPolicy(cancellation)
          : new CancellationPolicy(cancellation)
      )
    },
    isError: ({ _data: { cancellations } }: ShortTermCancellationPolicies) => {
      return !Array.isArray(cancellations) || !isCancellationsArray(cancellations)
    },
  }
) as unknown as
  | typeof ShortTermCancellationPoliciesError
  | typeof ShortTermCancellationPoliciesForReal

export type ShortTermCancellationPolicies = ShortTermCancellationPoliciesType
